import { useDisclosure } from '@mantine/hooks';

/**
 * The open/close state and derived flags shared by the goal action modals.
 */
export interface IUseGoalActionsResult {
  /** Whether the goal may be deleted (only when it has no logged conversions). */
  canDelete: boolean;
  isArchiveOpen: boolean;
  isDeleteOpen: boolean;
  isUnarchiveOpen: boolean;
  closeArchive: () => void;
  closeDelete: () => void;
  closeUnarchive: () => void;
  openArchive: () => void;
  openDelete: () => void;
  openUnarchive: () => void;
}

/**
 * Coordinates the archive, unarchive, and delete goal confirmation modals.
 *
 * Each action's business logic lives inside its own modal component; this hook
 * only owns which modal is open and whether delete is permitted, so the
 * list-row kebab and the detail-page action bar can share identical wiring.
 *
 * @param totalConversions - Conversions logged against the goal. Delete is
 * blocked while this is greater than zero so historical data is preserved.
 * @returns Modal open/close handlers and the `canDelete` flag.
 *
 * @example
 * const { isArchiveOpen, openArchive, closeArchive, canDelete } = useGoalActions(totalConversions);
 */
export function useGoalActions(totalConversions: number): IUseGoalActionsResult {
  const [isArchiveOpen, { open: openArchive, close: closeArchive }] = useDisclosure(false);
  const [isDeleteOpen, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [isUnarchiveOpen, { open: openUnarchive, close: closeUnarchive }] = useDisclosure(false);

  return {
    canDelete: totalConversions === 0,
    isArchiveOpen,
    isDeleteOpen,
    isUnarchiveOpen,
    closeArchive,
    closeDelete,
    closeUnarchive,
    openArchive,
    openDelete,
    openUnarchive,
  };
}
